import { Service } from '@roc/client-portal-shared/services';
import { FileUpload } from '@roc/ui';

const url = {
  SAVE_APPRAISAL_QUESTIONNAIRE: '/api/v1/roc-valuation/saveAppraisalQuestionnaire',
  SAVE_APPRAISAL_QUESTIONNAIRE_DOCUMENT: '/api/v1/roc-valuation/saveAppraisalQuestionnaireDocument',
  ESTIMATE_AMC_FEES: '/api/v1/roc-valuation/amcLender/getAmcsAndEstimatedFees',
  GET_VALUATION_DETAILS: '/api/v1/roc-valuation/getValuationDetails',
  DOWNLOAD_SIGNED_DOCUMENT: '/api/v1/roc-valuation/payment/downloadPaymentAuthorization',
  GET_PAYMENT_RECEIPT_DATA: '/api/v1/roc-valuation/payment/receiptData',
  CREATE_PAYMENT_LINK: '/api/v1/roc-valuation/createValuationPaymentLink',
  GET_PAYMENT_LINK: '/api/v1/roc-valuation/getValuationPaymentInfo',
  RESET_VALUATIONS: '/api/v1/roc-valuation/loan-property-valuation/resetValuationsByLoanId',
  CAN_RESET_VALUATIONS: '/api/v1/roc-valuation/loan-property-valuation/canResetValuations',
  RECONCILE_VALUATION_FEES: '/api/v1/roc-valuation/loan-property-valuation/reconcileValuationFees',
  GET_PROPERTY_VALUATIONS_BY_LOAN_ID: '/api/v1/roc-valuation/loan-valuation/getPropertyValuationsByLoanId',
  DOWNLOAD_VALUATION_REPORT: '/api/v1/roc-valuation/loan-valuation/downloadValuationReport',
  UPLOAD_VALUATION_REPORT: '/api/v1/loan/loanquotedocument/uploadValuationReport',
  CREATE_EXTRA_PAYMENT_LINK:
    '/api/v1/roc-valuation/createValuationExtraPaymentLink',
  SEND_ROV: '/api/v1/roc-valuation/platform/client/reference/sendROV',
  SEND_REVISION: '/api/v1/roc-valuation/platform/client/reference/sendRevision',
  REFUND_PAYMENT: '/api/v1/roc-valuation/payment/refundValuation',
  RESEND_PAYMENT_EMAIL: '/api/v1/roc-valuation/resendPaymentEmail',
  EDIT_PAYMENT: '/api/v1/roc-valuation/payment/editPayment',
  LOOKUP_ORDER: '/api/v1/roc-valuation/loan-property-valuation/lookupByOrderNumber',
  LINK_ORDER: '/api/v1/roc-valuation/loan-property-valuation/linkOrder',
  CREATE_LPV_AND_PAYMENT_LINK: '/api/v1/roc-valuation/loan-property-valuation/createLpvAndPaymentLink',
  PUSH_NEW_ORDER: '/api/v1/roc-valuation/loan-property-valuation/pushNewOrder',
  GET_RECERTIFICATION_PAYMENT_LINK: '/api/v1/roc-valuation/getRecertificationPaymentLink',
  START_DISPUTE: '/api/v1/roc-valuation/taskValuation/startDispute',
  SET_AS_SELECTED_PRIMARY: '/api/v1/roc-valuation/loan-valuation/setAsSelectedPrimary',
};

export interface GetValuationExtraPaymentLinkRequest {
  loanId: number;
  clientValuationReferenceIds: number[];
  fee: number;
}

export interface SendRevisionRequest {
  clientValuationReferenceId: number;
  revisionReason: string;
  revisionComment: string;
  file: File;
}

export interface AmcFeeEstimateProperty {
  state: string;
  county: string;
  propertyType: string;
  unitCount: number;
}

export interface EstimateAmcFeesRequest {
  lenderId: number;
  loanSubtype: string;
  properties: AmcFeeEstimateProperty[];
}

export interface AmcFeeEstimate {
  amcId: number;
  amcName: string;
  estimatedFee: number;
}

export class AppraisalDetailsV2Service extends Service {
  saveQuestionnaire = (data: any) => {
    return this.post(url.SAVE_APPRAISAL_QUESTIONNAIRE, data);
  };

  saveAppraisalQuestionnaireDocument = (data: FormData) => {
    return this.post(url.SAVE_APPRAISAL_QUESTIONNAIRE_DOCUMENT, data);
  };

  estimateAmcFees(request: EstimateAmcFeesRequest) {
    return this.post(url.ESTIMATE_AMC_FEES, request);
  }

  getValuationDetails(loanId?: number, loanPropertyValuationId?: number) {
    return this.get(url.GET_VALUATION_DETAILS, { loanId, loanPropertyValuationId });
  }

  editPayment(payment:any) {
    return this.post(url.EDIT_PAYMENT, payment);
  }

  downloadSignedDocument(paymentId: string) {
    return this.get(
      url.DOWNLOAD_SIGNED_DOCUMENT,
      {
        paymentId,
      },
      {
        responseType: 'arraybuffer',
      }
    );
  }

  getPaymentReceiptData(paymentId: number) {
    return this.get(url.GET_PAYMENT_RECEIPT_DATA, { paymentId });
  }

  createValuationPaymentLink(
    loanId: number,
    clientValuationReferenceIds: number[]
  ) {
    return this.post(url.CREATE_PAYMENT_LINK, {
      loanId,
      clientValuationReferenceIds,
    });
  }

  canResetValuations(loanId: number) {
    return this.get(url.CAN_RESET_VALUATIONS, { loanId });
  }

  reconcileValuationFees(loanId: number) {
    return this.get(url.RECONCILE_VALUATION_FEES, { loanId });
  }

  resetValuationsByLoanId(loanId: number) {
    return this.post(url.RESET_VALUATIONS + "?loanId=" + loanId, null);
  }

  getValuationPaymentLink(
    loanId: number,
    lenderId: number,
    paymentId: number,
    clientValuationReferenceIds: number[]
  ) {
    return this.post(url.GET_PAYMENT_LINK, {
      loanId,
      lenderId,
      paymentId,
      clientValuationReferenceIds,
    });
  }

  getPropertyValuationsByLoanId(loanId: number) {
    return this.get(url.GET_PROPERTY_VALUATIONS_BY_LOAN_ID, { loanId });
  }

  downloadValuationReport(
    loanId: number,
    propertyId: number,
    clientValuationReferenceId: number
  ) {
    return this.get(
      url.DOWNLOAD_VALUATION_REPORT,
      {
        loanId,
        propertyId,
        clientValuationReferenceId,
      },
      {
        responseType: 'arraybuffer',
      }
    );
  }

  submitReconsiderationOfValue(clientValuationReferenceId: number, message: string, fileUpload: FileUpload) {
    const config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
    const newFile = new File([blob], fileUpload.name, { type: fileUpload.file.type });
    const formData = new FormData();
    formData.append('clientValuationReferenceId', clientValuationReferenceId.toString());
    formData.append('reason', message);
    formData.append('file', newFile);
    return this.post(url.SEND_ROV, formData, config);
  }

  submitRevison(clientValuationReferenceId: number, message: string, fileUpload: FileUpload) {
    const config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
    const newFile = new File([blob], fileUpload.name, { type: fileUpload.file.type });
    const formData = new FormData();
    formData.append('clientValuationReferenceId', clientValuationReferenceId.toString());
    formData.append('reason', message);
    formData.append('file', newFile);
    return this.post(url.SEND_REVISION, formData, config);
  }

  createExtraPaymentLink(payload: GetValuationExtraPaymentLinkRequest) {
    return this.post(url.CREATE_EXTRA_PAYMENT_LINK, payload);
  }

  sendROV(payload: SendRevisionRequest) {
    return this.post(
      `${url.SEND_ROV}?clientReferenceId=${String(
        payload.clientValuationReferenceId
      )}`,
      payload
    );
  }

  uploadValuationReport(formData: FormData, loanQuoteId: number, propertyUUID: string) {
    return this.post(
      `${url.UPLOAD_VALUATION_REPORT}?loanQuoteId=${loanQuoteId}&propertyUUID=${propertyUUID}`,
      formData
    );
  }

  refundPayment(request: { paymentId: number; amount: number }) {
    return this.post(url.REFUND_PAYMENT, request);
  }

  resendPaymentEmail(paymentId: number | null) {
    return this.post(`${url.RESEND_PAYMENT_EMAIL}?paymentId=${paymentId}`, null);
  }

  lookupOrder(orderNumber: string, loanId: number) {
    return this.get(url.LOOKUP_ORDER, { orderNumber, loanId });
  }

  linkOrder(payload: {
    loanId: number;
    propertyId: number;
    orderNumber: string;
    isExistingOrder: boolean;
    appraisalType?: string;
    loanPropertyValuationId?: number | null;
    propertyValuationId?: number | null;
  }) {
    return this.post(url.LINK_ORDER, payload);
  }

  createLpvAndPaymentLink(payload: { loanId: number; propertyId: number; subType: string }) {
    return this.post(url.CREATE_LPV_AND_PAYMENT_LINK, payload);
  }

  pushNewOrder(payload: { loanId: number; propertyId: number; appraisalType: string }) {
    return this.post(url.PUSH_NEW_ORDER, payload);
  }

  getRecertificationPaymentLink(loanId: number, propertyId: number) {
    return this.get(url.GET_RECERTIFICATION_PAYMENT_LINK, { loanId, propertyId });
  }

  startDispute(clientValuationReferenceId: number, reason: string, disputeType: string, isInternalUser: boolean) {
    return this.post(
      `${url.START_DISPUTE}?clientValuationReferenceId=${clientValuationReferenceId}&reason=${encodeURIComponent(reason)}&disputeType=${encodeURIComponent(disputeType)}&isInternalUser=${isInternalUser}`,
      null
    );
  }

  setAsSelectedPrimary(loanPropertyValuationId: number) {
    return this.put(
      `${url.SET_AS_SELECTED_PRIMARY}?loanPropertyValuationId=${loanPropertyValuationId}`,
      null
    );
  }
}
